import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { MultiLineChart } from "@/shared/charts/MultiLineChart";
import { SingleLineChart } from "@/shared/charts/SingleLineChart";
import { HeatLinkButton } from "@/shared/data-entries/HeatLinkButton";
import { NotesFieldDE } from "@/shared/data-entries/common/NotesFieldDE";
import { ProcessDataCheckboxFieldDE } from "@/shared/data-entries/process-data/ProcessDataCheckboxField";
import { GradeSelector } from "@/shared/selectors/GradeSelector";
import { HeatSelector } from "@/shared/selectors/HeatSelector";
import { formatProductionDate } from "@/utils/date-time-formatter";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Box, Button, Grid, Paper, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { NotificationSnackBar } from "../../../controls/snackbars/notification-snackbar";
import { RefreshButtons } from "../../../shared/data-entries/RefreshButtons";
import { StatusTitle } from "../../../shared/data-entries/StatusTitle";
import { ProcessDataDateTimeFieldDE } from "../../../shared/data-entries/process-data/ProcessDataDateTimeFieldDE";
import { ProcessDataTextFieldDE } from "../../../shared/data-entries/process-data/ProcessDataTextFieldDE";
import { ProcessDataTextFieldWithCheckbox } from "../../../shared/data-entries/process-data/ProcessDataTextFieldWithCheckbox";
import { LadleSelector } from "../../../shared/selectors/LadleSelector";
import { OperatorSelector } from "../../../shared/selectors/OperatorSelector";
import { TundishSelector } from "../../../shared/selectors/TundishSelector";
import { TundishShroudSelector } from "../../../shared/selectors/TundishShroudSelector";
import { TableChemistry } from "../../laboratory/data-entry/components/tables/chemistries/TableChemistry";
import { ProcessNavigator } from "@/shared/data-entries/ProcessNavigator";
import {
  getLastestProcesses,
  searchProcesses,
} from "./repositories/caster.repositories";
import { ArriveButtons } from "./components/buttons/ArriveButtons";
import { CustomButton } from "./components/buttons/Buttons";
import { LinkCasterProcess } from "./components/modals/LinkCasterDE";
import { PourbackModal } from "./components/modals/pourback/PourbackModal";
import { TableProbes } from "./components/tables/table-probes/TableProbes";
import { TableStrands } from "./components/tables/table-strands/TableStrands";
import { useDataEntryContext } from "./context/reducer.provider";
import { MissingFieldsBanner } from "./components/MissingFieldsBanner";
import { StrandLossWarnings } from "./components/strand-loss-warnings/StrandLossWarnings";
import { StrandLossJustifyModal } from "./components/modals/StrandLossJustifyModal";
import { useStrandLossJustify } from "./hooks/useStrandLossJustify";
import { getPendingStrandLossWarnings } from "./utils/strand-losses/getPendingStrandLossWarnings";
import { JustifyAllStrandLossesButton } from "./components/buttons/JustifyAllStrandLossesButton";
import { JustifyAllStrandLossesModal } from "./components/modals/JustifyAllStrandLossesModal";
import { useJustifyAllStrandLosses } from "./hooks/useJustifyAllStrandLosses";
import { useTimerContext } from "./context/timer.provider";
import { useCasterDataEntryActions } from "./hooks/useCasterDataEntryActions";
import { useLinkUnlinkProcess } from "./hooks/useLinkUnlinkProcess";
import { useRealtimeRefresh } from "./hooks/useRealtimeRefresh";
import { Process } from "./models/caster.models";
import { useTheme } from "@mui/material/styles";
import { dataEntryStyles } from "../../../shared/styles/dataEntryStyles";

const isValidDate = (date: any): boolean => {
  if (date === null || date === undefined) return false;
  const dateObj = new Date(date);
  return !isNaN(dateObj.getTime());
};

const getDateValidationErrors = (
  date: Date | null,
  fieldName: string,
): string | true => {
  if (date === null || date === undefined) return `${fieldName} is required`;
  if (!isValidDate(date)) return `${fieldName} must be a valid date`;
  if (isFutureDate(date)) return `${fieldName} cannot be in the future`;
  return true;
};

const isFutureDate = (date: Date | null): boolean => {
  if (!date) return false;
  return new Date(date) > new Date();
};

function secondsToHHMM(seconds: number) {
  seconds = Math.abs(Math.floor(seconds));

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");

  return `${formattedHours}:${formattedMinutes}`;
}

interface FormValues {
  Start: Date | null;
  LadleOpen: Date | null;
  LadleClose: Date | null;
  End: Date | null;
}

export const CasterDataEntryPage = () => {
  const theme = useTheme();
  const styles = dataEntryStyles(theme);

  const navigate = useNavigate();
  const params = useParams();

  const { seconds, isRunning, pause, restart, resume } = useTimerContext();

  const {
    changeHeatStatus,
    loadData,
    refreshData,
    saveProcess,
    saveProcessData,
    saveHeat,
  } = useCasterDataEntryActions();

  // Real-time: refresh process data when a mold goes online/offline
  useRealtimeRefresh(() => refreshData());
  const {
    state: {
      deProcess,
      notificationMessage,
      isDataLoading,
      errorMessage,
      lastUpdate,
    },
    actions: { setProcess, setNotificationMessage, setErrorMessage },
  } = useDataEntryContext();

  const process = deProcess?.process;
  const missingFieldNames = new Set(
    deProcess?.missingFields?.map((f) => f.FieldName) ?? [],
  );

  const {
    handleLinkHeat,
    handleUnlinkHeat,
    showLinkModal,
    showUnlinkModal,
    unlinkProcess,
    setShowLinkModal,
  } = useLinkUnlinkProcess(process || ({} as Process));

  const { openStrandLossById } = useStrandLossJustify();
  const strandLossWarnings = getPendingStrandLossWarnings(
    deProcess?.previousPendingStrandLosses ?? [],
    deProcess?.strands ?? [],
  );
  const { canJustifyAll, isJustifyingAll, openJustifyAll } =
    useJustifyAllStrandLosses();

  const [showArrive, setShowArrive] = useState(false);
  const [pourbackModalOpen, setPourbackModalOpen] = useState(false);
  const [isLadleOpen, setIsLadleOpen] = useState(false);
  const [isLadleClose, setIsLadleClose] = useState(false);
  const [disableButtons, setDisableButtons] = useState(false);

  const { control, reset, clearErrors } = useForm<FormValues>({
    mode: "onChange",
  });

  useEffect(() => {
    if (process) {
      reset({
        Start: process.Start,
        LadleOpen: process.LadleOpen,
        LadleClose: process.LadleClose,
        End: process.End,
      });
    }
  }, [process]);

  useEffect(() => {
    if (process?.LadleOpen) {
      setIsLadleOpen(true);
    } else {
      setIsLadleOpen(false);
    }

    if (process?.LadleClose) {
      setIsLadleClose(true);
    } else {
      setIsLadleClose(false);
    }
  }, [process?.LadleOpen, process?.LadleClose]);

  useEffect(() => {
    clearErrors();
  }, [process?.ProcessId]);

  return (
    <>
      <HelmetDexteel
        title={`${
          process?.HeatName ? `${process.HeatName} - ` : ""
        }Caster Data Entry`}
      />
      {deProcess?.missingFields && deProcess.missingFields.length > 0 && (
        <MissingFieldsBanner missingFields={deProcess.missingFields} />
      )}
      <Box
        padding={2}
        style={{
          height: "calc(100vh - 64px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Grid container spacing={1} style={{ flex: 1 }}>
          <Grid item xs={12}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Caster Data Entry
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Paper>
              <Box padding={1}>
                <Grid
                  item
                  container
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  spacing={3}
                >
                  <Grid item xs={12} md={2}>
                    <Grid container direction="column" spacing={1}>
                      <Grid item>
                        <ProcessNavigator
                          key={`caster-de-process-navigator`}
                          process={deProcess?.process ?? null}
                          loadData={loadData}
                          getLatestProcesses={getLastestProcesses}
                          searchProcesses={searchProcesses}
                          pause={pause}
                          resume={resume}
                          onError={setErrorMessage}
                          initialProcessId={
                            params.processId
                              ? Number(params.processId)
                              : undefined
                          }
                        />
                      </Grid>
                      <Grid item sx={{ mt: -2 }}>
                        <Grid
                          container
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <Grid item md={10}>
                            <StatusTitle
                              heatStatus={process?.HeatStatusCode ?? ""}
                            />
                          </Grid>
                          <Grid
                            item
                            md={2}
                            sx={{ display: "flex", justifyContent: "center" }}
                          >
                            <HeatLinkButton
                              isLinked={process?.HeatId != null}
                              disabled={!process}
                              onLink={() => {
                                pause();
                                if (process) {
                                  handleLinkHeat();
                                }
                              }}
                              onUnlink={async () => {
                                pause();
                                if (process) {
                                  await unlinkProcess();
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item sx={{ mt: 1.5 }}>
                        <StrandLossWarnings
                          warnings={strandLossWarnings}
                          onOpenStrandLoss={openStrandLossById}
                          action={
                            canJustifyAll ? (
                              <JustifyAllStrandLossesButton
                                isJustifying={isJustifyingAll}
                                onJustifyAll={openJustifyAll}
                                iconFontSize={28}
                              />
                            ) : null
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    key={`caster-data-entry-first-${process?.ProcessId}`}
                    item
                    xs={12}
                    sm={6}
                    md={2}
                  >
                    <Grid container direction={"column"} spacing={1}>
                      <Grid item container spacing={1}>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label={"Shift"}
                            variant={"outlined"}
                            disabled
                            value={`${formatProductionDate(process?.ProductionDate)} ${process?.Shift} - ${process?.Crew}`}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            value={secondsToHHMM(process?.Duration ?? 0)}
                            label="Cast Time"
                            variant="outlined"
                            fullWidth
                            autoComplete="off"
                            disabled
                          />
                        </Grid>
                      </Grid>
                      <Grid item container spacing={1}>
                        <Grid item xs={12} md={6}>
                          <LadleSelector
                            disableClearable
                            disabled={process?.HeatId ? false : true}
                            value={process?.LadleId || null}
                            highlight={missingFieldNames.has("LadleId")}
                            onChange={(value: number | null) => {
                              saveHeat({
                                heatId: process?.HeatId!,
                                field: "LadleId",
                                value,
                              });
                            }}
                            onOpen={pause}
                            onClose={resume}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label={"Ladle Life"}
                            variant={"outlined"}
                            disabled
                            value={
                              process?.HeatsOnLadle ? process?.HeatsOnLadle : ""
                            }
                          />
                        </Grid>
                      </Grid>
                      <Grid item container spacing={1}>
                        <Grid item xs={12} md={6}>
                          <TundishSelector
                            disableClearable
                            disabled={!process?.HeatId}
                            value={process?.TundishId || null}
                            highlight={missingFieldNames.has("TundishId")}
                            onChange={(value) => {
                              saveProcessData({
                                processId: process?.ProcessId!,
                                field: "TundishId",
                                value: value,
                              });
                            }}
                            onOpen={pause}
                            onClose={resume}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <ProcessDataTextFieldWithCheckbox<Process>
                            textFieldLabel="Heats"
                            textField={"HeatsOnTundish"}
                            checkboxLabel={"First on Tundish"}
                            disabled
                            readOnly={true}
                            textFieldValue={process?.HeatsOnTundish}
                            checkboxValue={process?.IsFirstOnTundish}
                            onChangeCheckbox={(value) => {
                              saveProcessData({
                                processId: process?.ProcessId!,
                                field: "IsFirstOnTundish",
                                value: value,
                              });
                            }}
                          />
                        </Grid>
                      </Grid>
                      <Grid item container spacing={1}>
                        <Grid item xs={12} md={6}>
                          <TundishShroudSelector
                            disableClearable
                            disabled={process?.HeatId ? false : true}
                            value={process?.TundishShroudId || null}
                            highlight={missingFieldNames.has("TundishShroudId")}
                            onChange={(value) => {
                              saveProcessData({
                                processId: process?.ProcessId!,
                                field: "TundishShroudId",
                                value: value,
                              });
                            }}
                            onOpen={pause}
                            onClose={resume}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <ProcessDataTextFieldWithCheckbox<Process>
                            textFieldLabel="Heats"
                            textField={"HeatsOnTundishShroud"}
                            checkboxLabel={"First on Shrouded"}
                            disabled
                            readOnly={true}
                            textFieldValue={process?.HeatsOnTundishShroud}
                            checkboxValue={process?.IsShrouded}
                            onChangeCheckbox={(value) => {
                              saveProcessData({
                                processId: process?.ProcessId!,
                                field: "IsShrouded",
                                value: value,
                              });
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid
                    key={`caster-data-entry-sec-${process?.ProcessId}`}
                    item
                    xs={12}
                    sm={6}
                    md={2}
                  >
                    <Grid container direction={"column"} spacing={1}>
                      <Grid item>
                        <GradeSelector
                          disableClearable
                          disabled={!process?.HeatId}
                          value={process?.GradeId ?? null}
                          highlight={missingFieldNames.has("GradeId")}
                          onChange={(value) => {
                            if (process?.HeatId) {
                              saveHeat({
                                heatId: process.HeatId,
                                field: "GradeId",
                                value,
                              });
                            }
                          }}
                          onFocus={pause}
                          onBlur={resume}
                        />
                      </Grid>
                      <Grid item>
                        <OperatorSelector
                          disableClearable
                          value={process?.OperatorForemanId || null}
                          assetId={process?.AssetId ?? 0}
                          onChange={(value: number | null) => {
                            saveProcessData({
                              processId: process?.ProcessId
                                ? process?.ProcessId
                                : 0,
                              field: "OperatorForemanId",
                              value,
                            });
                          }}
                          label={"Foreman"}
                          onlyForemans={true}
                          onFocus={pause}
                          onBlur={() => {
                            resume();
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <ProcessDataTextFieldDE<Process>
                          field={"ForemanFillInName"}
                          label={"Fill In"}
                          value={process?.ForemanFillInName}
                          onChange={(value) => {
                            setProcess({
                              ...process,
                              ["ForemanFillInName"]: value,
                            });
                          }}
                          onBlur={() => {
                            saveProcessData({
                              processId: process?.ProcessId!,
                              field: "ForemanFillInName",
                              value: process!["ForemanFillInName"],
                            }).then(() => {
                              resume();
                            });
                          }}
                          onFocus={pause}
                        />
                      </Grid>
                      <Grid item>
                        <OperatorSelector
                          disableClearable
                          value={process?.OperatorId || null}
                          assetId={process?.AssetId ?? 0}
                          onChange={(value: number | null) => {
                            saveProcessData({
                              processId: process?.ProcessId
                                ? process?.ProcessId
                                : 0,
                              field: "OperatorId",
                              value,
                            });
                          }}
                          label={"Operator"}
                          onFocus={pause}
                          onBlur={() => {
                            resume();
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    key={`caster-data-entry-third-${process?.ProcessId}`}
                    item
                    xs={12}
                    sm={6}
                    md={2}
                  >
                    <Grid container direction={"column"} spacing={1}>
                      <Grid item>
                        <Controller
                          control={control}
                          name="Start"
                          rules={{
                            required: "Arrive datetime is required",
                            validate: {
                              validDate: (value) =>
                                !value ||
                                getDateValidationErrors(value, "Start"),
                            },
                          }}
                          render={({
                            field: { onChange, value },
                            fieldState: { error },
                          }) => (
                            <>
                              <ProcessDataDateTimeFieldDE<Process>
                                value={value}
                                field={"Start"}
                                label="Arrive"
                                format="MM-DD-YYYY HH:mm"
                                clearable={false}
                                onChange={(date) => {
                                  onChange(date);
                                  setProcess({ ...process, Start: date });
                                }}
                                onBlur={(value) => {
                                  if (!error && value) {
                                    saveProcess({
                                      processId: process?.ProcessId!,
                                      field: "Start",
                                      value: value,
                                    });
                                    resume();
                                  }
                                }}
                                onFocus={() => {
                                  setTimeout(() => {
                                    pause();
                                  }, 0);
                                }}
                              />
                              {error && (
                                <Typography variant="caption" color="error">
                                  {error.message}
                                </Typography>
                              )}
                            </>
                          )}
                        />
                      </Grid>
                      <Grid item>
                        <Controller
                          control={control}
                          name="LadleOpen"
                          rules={{
                            required: "Ladle Open datetime is required",
                            validate: {
                              validDate: (value) =>
                                !value ||
                                getDateValidationErrors(value, "Ladle Open"),
                              afterStart: (value, formValues) => {
                                if (!value || !formValues.Start) return true;
                                return (
                                  value > formValues.Start ||
                                  "Ladle Open must be after Arrive"
                                );
                              },
                              beforeClose: (value, formValues) => {
                                if (!value || !formValues.LadleClose)
                                  return true;
                                return (
                                  value < formValues.LadleClose ||
                                  "Ladle Open must be before Ladle Close"
                                );
                              },
                            },
                          }}
                          render={({
                            field: { onChange, value },
                            fieldState: { error },
                          }) => (
                            <>
                              <ProcessDataDateTimeFieldDE<Process>
                                value={value}
                                field={"LadleOpen"}
                                label="Ladle Open"
                                format="MM-DD-YYYY HH:mm"
                                disabled={!isLadleOpen}
                                clearable={false}
                                onChange={(date) => {
                                  onChange(date);
                                  setProcess({ ...process, LadleOpen: date });
                                }}
                                onBlur={(value) => {
                                  if (!error) {
                                    saveProcessData({
                                      processId: process?.ProcessId!,
                                      field: "LadleOpen",
                                      value: value,
                                    });
                                    resume();
                                  }
                                }}
                                onFocus={() => {
                                  setTimeout(() => {
                                    pause();
                                  }, 0);
                                }}
                              />
                              {error && (
                                <Typography variant="caption" color="error">
                                  {error.message}
                                </Typography>
                              )}
                            </>
                          )}
                        />
                      </Grid>
                      <Grid item>
                        <Controller
                          control={control}
                          name="LadleClose"
                          rules={{
                            required: "Ladle Close datetime is required",
                            validate: {
                              validDate: (value) =>
                                !value ||
                                getDateValidationErrors(value, "Ladle Close"),
                              afterOpen: (value, formValues) => {
                                if (!value || !formValues.LadleOpen)
                                  return true;
                                return (
                                  value > formValues.LadleOpen ||
                                  "Ladle Close must be after Ladle Open"
                                );
                              },
                              beforeEnd: (value, formValues) => {
                                if (!value || !formValues.End) return true;
                                return (
                                  value < formValues.End ||
                                  "Ladle Close must be before End"
                                );
                              },
                            },
                          }}
                          render={({
                            field: { onChange, value },
                            fieldState: { error },
                          }) => (
                            <>
                              <ProcessDataDateTimeFieldDE<Process>
                                value={value}
                                field={"LadleClose"}
                                label="Ladle Close"
                                format="MM-DD-YYYY HH:mm"
                                disabled={!isLadleClose}
                                clearable={false}
                                onChange={(date) => {
                                  onChange(date);
                                  setProcess({ ...process, LadleClose: date });
                                }}
                                onBlur={(value) => {
                                  if (!error) {
                                    saveProcessData({
                                      processId: process?.ProcessId!,
                                      field: "LadleClose",
                                      value: value,
                                    });
                                    resume();
                                  }
                                }}
                                onFocus={() => {
                                  setTimeout(() => {
                                    pause();
                                  }, 0);
                                }}
                              />
                              {error && (
                                <Typography variant="caption" color="error">
                                  {error.message}
                                </Typography>
                              )}
                            </>
                          )}
                        />
                      </Grid>
                      <Grid item>
                        <Controller
                          control={control}
                          name="End"
                          rules={{
                            required:
                              process?.HeatStatusCode === "F"
                                ? "End datetime is required"
                                : false,
                            validate: {
                              validDate: (value) =>
                                !value || getDateValidationErrors(value, "End"),
                              afterClose: (value, formValues) => {
                                if (!value || !formValues.LadleClose)
                                  return true;
                                return (
                                  value >= formValues.LadleClose ||
                                  "End must be after Ladle Close"
                                );
                              },
                            },
                          }}
                          render={({
                            field: { onChange, value },
                            fieldState: { error },
                          }) => (
                            <>
                              <ProcessDataDateTimeFieldDE<Process>
                                value={value}
                                field={"End"}
                                label="End"
                                format="MM-DD-YYYY HH:mm"
                                disabled={!isLadleClose}
                                clearable={false}
                                onChange={(date) => {
                                  onChange(date);
                                  setProcess({ ...process, End: date });
                                }}
                                onBlur={(value) => {
                                  if (!error) {
                                    saveProcess({
                                      processId: process?.ProcessId!,
                                      field: "End",
                                      value: value,
                                    });
                                    resume();
                                  }
                                }}
                                onFocus={() => {
                                  setTimeout(() => {
                                    pause();
                                  }, 0);
                                }}
                              />
                              {error && (
                                <Typography variant="caption" color="error">
                                  {error.message}
                                </Typography>
                              )}
                            </>
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {deProcess?.process && (
                    <ArriveButtons
                      process={deProcess?.process}
                      disableButtons={disableButtons}
                      setDisableButtons={setDisableButtons}
                      changeHeatStatus={changeHeatStatus}
                      refreshData={refreshData}
                      loadData={loadData}
                      pause={pause}
                      setShowArrive={setShowArrive}
                      setIsLadleOpen={setIsLadleOpen}
                      setIsLadleClose={setIsLadleClose}
                      setErrorMessage={setErrorMessage}
                      saveProcess={saveProcess}
                      saveProcessData={saveProcessData}
                    />
                  )}
                  <Grid
                    key={`caster-data-entry-fifth-${process?.ProcessId}`}
                    item
                    xs={12}
                    sm={12}
                    md={2}
                  >
                    <Grid item container direction={"column"} spacing={2}>
                      <Grid item xs={12} sm={12} md={12} sx={{ mt: 1 }}>
                        <RefreshButtons
                          seconds={seconds}
                          isRunning={isRunning}
                          pause={pause}
                          restart={restart}
                          resume={resume}
                          refreshData={refreshData}
                          isDataLoading={isDataLoading}
                          process={deProcess?.process}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} sx={{ mt: 0.5 }}>
                        <Button
                          fullWidth
                          variant={"outlined"}
                          size="large"
                          onClick={() =>
                            navigate(
                              `/caster-processes/${process?.ShiftId}?processId=${process?.ProcessId}`,
                            )
                          }
                          sx={styles.buttons}
                        >
                          Processes
                        </Button>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        sx={{ mt: 0.5, mb: 0.8 }}
                      >
                        <Button
                          fullWidth
                          variant={"outlined"}
                          size="large"
                          onClick={() =>
                            navigate(`/caster-torches/${process?.ProcessId}`)
                          }
                          sx={styles.buttons}
                        >
                          Torches Data Entry
                        </Button>
                      </Grid>
                      <Grid item container spacing={1}>
                        <Grid item xs={6} sm={6} md={6}>
                          <ProcessDataCheckboxFieldDE<Process>
                            field={"IsManualOpen"}
                            label="Manual Opened"
                            value={process?.IsManualOpen}
                            onChange={(value) => {
                              saveProcessData({
                                processId: process?.ProcessId!,
                                field: "IsManualOpen",
                                value: value,
                              });
                            }}
                            disabled={isDataLoading}
                          />
                        </Grid>
                        <Grid item xs={6} sm={6} md={6}>
                          <ProcessDataCheckboxFieldDE<Process>
                            field={"IsLadleShrouded"}
                            label="Ladle Shrouded"
                            value={process?.IsLadleShrouded}
                            onChange={(value) => {
                              saveProcessData({
                                processId: process?.ProcessId!,
                                field: "IsLadleShrouded",
                                value: value,
                              });
                            }}
                            disabled={isDataLoading}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={3}>
                <TableProbes />
              </Grid>
              <Grid item xs={12} md={9}>
                <TableStrands />
              </Grid>
            </Grid>
          </Grid>
          {/* ROW 3 */}
          <Grid item container spacing={1}>
            <Grid item xs={12} md={3}>
              <SingleLineChart
                title={"Tundish Pre-Heating Temp"}
                data={deProcess?.tundishTemperatures ?? []}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SingleLineChart
                title={"Pad Impact Temperature"}
                data={deProcess?.padTemperatures ?? []}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SingleLineChart
                title={"Steel Temperature"}
                data={deProcess?.steelTemperatures ?? []}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Grid container direction="column" spacing={1}>
                <Grid item>
                  <Paper>
                    <Box
                      padding={1}
                      style={{
                        border: "1px solid #ccc",
                        borderRadius: 4,
                        padding: "10px 10px 10px 10px",
                      }}
                    >
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} md={8} sx={{ mt: 0.5 }}>
                          <CustomButton
                            disabled={!process?.HeatId}
                            onClick={() => {
                              pause();
                              setPourbackModalOpen(true);
                            }}
                            label="Declare Pourback"
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            variant={"outlined"}
                            disabled
                            label={"Weight [tons]"}
                            value={process?.PourbackWeight ?? ""}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item>
                  <Paper>
                    <Box
                      padding={1}
                      style={{
                        border: "1px solid #ccc",
                        borderRadius: 4,
                        padding: "10px 10px 10px 10px",
                        height: "272px",
                        minHeight: "272px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Grid
                        container
                        spacing={1}
                        style={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          flexWrap: "nowrap",
                        }}
                      >
                        <Grid item xs={12} style={{ flex: "0 0 auto" }}>
                          <Grid container spacing={1}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                variant={"outlined"}
                                disabled
                                label={"Total Cast Weight [tons]"}
                                value={process?.CastTons?.toFixed(1) ?? ""}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                variant={"outlined"}
                                disabled
                                label={"Total Scrap Weight [lbs]"}
                                value={process?.ScrapTons?.toFixed(1) ?? ""}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          style={{
                            flex: "1 1 auto",
                            minHeight: 0,
                            display: "flex",
                          }}
                        >
                          <NotesFieldDE
                            value={process?.Comments ?? ""}
                            onChange={(value) => {
                              setProcess({
                                ...process,
                                Comments: value,
                              });
                            }}
                            onBlur={() => {
                              saveProcessData({
                                processId: process?.ProcessId!,
                                field: "Comments",
                                value: process?.Comments ?? "",
                              }).then(() => {
                                resume();
                              });
                            }}
                            onFocus={pause}
                            minRows={10}
                            maxRows={10}
                            height="100%"
                            resize="none"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
            {/* COLUMN 1 */}
            <Grid item xs={12} md={6}>
              <Grid container direction={"column"} spacing={1}>
                <Grid item xs={12} md={12}>
                  <MultiLineChart
                    title={"Strands Speed"}
                    data={deProcess?.strandSpeeds ?? []}
                  />
                </Grid>
              </Grid>
            </Grid>
            {/* COLUMN 2 */}
            <Grid item xs={12} md={6}>
              <Grid container direction={"column"} spacing={1}>
                <Grid item xs={12} md={12}>
                  <MultiLineChart
                    title={"Molds Level"}
                    data={deProcess?.strandMoldLevels ?? []}
                    valueUnit="%"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <TableChemistry
                key={`caster-data-entry-${deProcess?.process.HeatId}`}
                heatId={deProcess?.process.HeatId ?? null}
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <HeatSelector
        show={showArrive}
        onHide={() => {
          setShowArrive(false);
          resume();
        }}
        assetId={process?.AssetId ?? 0}
        changeHeatStatus={changeHeatStatus}
      />

      <PourbackModal
        show={pourbackModalOpen}
        onHide={(shouldUpdate) => {
          setPourbackModalOpen(false);
          resume();
          if (shouldUpdate) refreshData();
        }}
        process={process}
      />

      <ConfirmationDialogRaw
        title="Confirmation"
        message={showUnlinkModal.message}
        open={showUnlinkModal.show}
        onClose={handleUnlinkHeat}
      />
      <StrandLossJustifyModal />
      <JustifyAllStrandLossesModal />
      <LinkCasterProcess
        show={showLinkModal}
        onHide={() => {
          setShowLinkModal(false);
          resume();
        }}
      />

      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={errorMessage}
        onHide={() => setErrorMessage("")}
        title="Unexpected error"
      />
    </>
  );
};
